import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import employeeService from '../../services/employeeService';
import timesheetService from '../../services/timesheetService';
import payrollService from '../../services/payrollService';

const TimesheetManagementPortal = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('work');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [payPeriod, setPayPeriod] = useState('');

  // Form data for each tab
  const [workData, setWorkData] = useState({
    date: '',
    type: '',
    units: '0.0000',
    rate: '0.000000',
    value: '0.00'
  });

  const [leaveTakenData, setLeaveTakenData] = useState({
    date: '',
    type: '',
    units: '0.0000',
    rate: '0.000000',
    value: '0.00'
  });

  const [allowancesData, setAllowancesData] = useState({
    date: '',
    type: '',
    units: '0.0000',
    rate: '0.000000',
    value: '0.00'
  });

  const [recurringDeductData, setRecurringDeductData] = useState({
    date: '',
    type: '',
    units: '0.0000',
    rate: '0.000000',
    value: '0.00'
  });

  const [sundryDeductData, setSundryDeductData] = useState({
    date: '',
    type: '',
    units: '0.0000',
    rate: '0.000000',
    value: '0.00'
  });

  const [overheadsData, setOverheadsData] = useState({
    date: '',
    type: '',
    units: '0.0000',
    rate: '0.000000',
    value: '0.00'
  });

  const [payslipNote, setPayslipNote] = useState('');
  const [payrollSummary, setPayrollSummary] = useState(null);

  useEffect(() => {
    loadEmployees();
    setDefaultPayPeriod();
  }, []);

  useEffect(() => {
    if (selectedEmployee) {
      loadEmployeePayrollData();
    }
  }, [selectedEmployee]);

  const setDefaultPayPeriod = () => {
    const today = new Date();
    const month = String(today?.getMonth() + 1)?.padStart(2, '0');
    const year = today?.getFullYear();
    setPayPeriod(`Payrun ending ${today?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} for payment on ${today?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`);
  };

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const data = await employeeService?.getAll();
      const activeEmployees = data?.filter(e => e?.isActive) || [];
      setEmployees(activeEmployees);
      
      if (activeEmployees?.length > 0) {
        setSelectedEmployee(activeEmployees?.[0]);
      }
    } catch (err) {
      console.error('Error loading employees:', err);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeePayrollData = async () => {
    if (!selectedEmployee) return;

    try {
      // Load existing payroll data for the selected employee
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      const timesheetRecords = await timesheetService?.getAllTimesheetRecords(
        startOfMonth?.toISOString()?.split('T')?.[0],
        endOfMonth?.toISOString()?.split('T')?.[0]
      );

      const employeeRecords = timesheetRecords?.filter(r => r?.employeeId === selectedEmployee?.id) || [];

      // Calculate summary
      const totalHours = employeeRecords?.reduce((sum, r) => sum + (parseFloat(r?.totalHours) || 0), 0);
      const totalOvertime = employeeRecords?.reduce((sum, r) => sum + (parseFloat(r?.overtimeHours) || 0), 0);
      
      setPayrollSummary({
        regularHours: totalHours - totalOvertime,
        overtimeHours: totalOvertime,
        totalHours: totalHours,
        casualTax: 0,
        resident: 2
      });
    } catch (err) {
      console.error('Error loading payroll data:', err);
    }
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setError('');
    setSuccessMessage('');
  };

  const filteredEmployees = employees?.filter(emp => {
    const searchLower = searchQuery?.toLowerCase();
    const fullName = `${emp?.firstName || ''} ${emp?.lastName || ''}`?.toLowerCase();
    const empNumber = (emp?.employeeNumber || '')?.toLowerCase();
    return fullName?.includes(searchLower) || empNumber?.includes(searchLower);
  });

  const calculateValue = (units, rate) => {
    const u = parseFloat(units) || 0;
    const r = parseFloat(rate) || 0;
    return (u * r)?.toFixed(2);
  };

  const handleWorkDataChange = (field, value) => {
    const newData = { ...workData, [field]: value };
    if (field === 'units' || field === 'rate') {
      newData.value = calculateValue(newData?.units, newData?.rate);
    }
    setWorkData(newData);
  };

  const handleLeaveTakenDataChange = (field, value) => {
    const newData = { ...leaveTakenData, [field]: value };
    if (field === 'units' || field === 'rate') {
      newData.value = calculateValue(newData?.units, newData?.rate);
    }
    setLeaveTakenData(newData);
  };

  const handleAllowancesDataChange = (field, value) => {
    const newData = { ...allowancesData, [field]: value };
    if (field === 'units' || field === 'rate') {
      newData.value = calculateValue(newData?.units, newData?.rate);
    }
    setAllowancesData(newData);
  };

  const handleRecurringDeductDataChange = (field, value) => {
    const newData = { ...recurringDeductData, [field]: value };
    if (field === 'units' || field === 'rate') {
      newData.value = calculateValue(newData?.units, newData?.rate);
    }
    setRecurringDeductData(newData);
  };

  const handleSundryDeductDataChange = (field, value) => {
    const newData = { ...sundryDeductData, [field]: value };
    if (field === 'units' || field === 'rate') {
      newData.value = calculateValue(newData?.units, newData?.rate);
    }
    setSundryDeductData(newData);
  };

  const handleOverheadsDataChange = (field, value) => {
    const newData = { ...overheadsData, [field]: value };
    if (field === 'units' || field === 'rate') {
      newData.value = calculateValue(newData?.units, newData?.rate);
    }
    setOverheadsData(newData);
  };

  const handleSaveData = async () => {
    if (!selectedEmployee) {
      setError('Please select an employee');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      // Save data based on active tab
      let dataToSave = {};
      let entryType = '';

      switch (activeTab) {
        case 'work':
          dataToSave = workData;
          entryType = 'work';
          break;
        case 'leave':
          dataToSave = leaveTakenData;
          entryType = 'leave';
          break;
        case 'allowances':
          dataToSave = allowancesData;
          entryType = 'allowance';
          break;
        case 'recurring':
          dataToSave = recurringDeductData;
          entryType = 'recurring_deduction';
          break;
        case 'sundry':
          dataToSave = sundryDeductData;
          entryType = 'sundry_deduction';
          break;
        case 'overheads':
          dataToSave = overheadsData;
          entryType = 'overhead';
          break;
      }

      if (!dataToSave?.date) {
        setError('Please enter a date');
        return;
      }

      // Create payroll entry
      const entry = {
        employeeId: selectedEmployee?.id,
        entryType: entryType,
        date: dataToSave?.date,
        type: dataToSave?.type,
        units: parseFloat(dataToSave?.units) || 0,
        rate: parseFloat(dataToSave?.rate) || 0,
        value: parseFloat(dataToSave?.value) || 0,
        notes: payslipNote
      };

      await payrollService?.createPayrollEntry(entry);
      
      setSuccessMessage('Payroll entry saved successfully');
      
      // Reload payroll data
      await loadEmployeePayrollData();

      // Clear form
      switch (activeTab) {
        case 'work':
          setWorkData({ date: '', type: '', units: '0.0000', rate: '0.000000', value: '0.00' });
          break;
        case 'leave':
          setLeaveTakenData({ date: '', type: '', units: '0.0000', rate: '0.000000', value: '0.00' });
          break;
        case 'allowances':
          setAllowancesData({ date: '', type: '', units: '0.0000', rate: '0.000000', value: '0.00' });
          break;
        case 'recurring':
          setRecurringDeductData({ date: '', type: '', units: '0.0000', rate: '0.000000', value: '0.00' });
          break;
        case 'sundry':
          setSundryDeductData({ date: '', type: '', units: '0.0000', rate: '0.000000', value: '0.00' });
          break;
        case 'overheads':
          setOverheadsData({ date: '', type: '', units: '0.0000', rate: '0.000000', value: '0.00' });
          break;
      }
      setPayslipNote('');

    } catch (err) {
      console.error('Error saving payroll data:', err);
      setError(err?.message || 'Failed to save payroll data');
    } finally {
      setSaving(false);
    }
  };

  const renderTabContent = () => {
    let currentData = {};
    let handleChange = () => {};

    switch (activeTab) {
      case 'work':
        currentData = workData;
        handleChange = handleWorkDataChange;
        break;
      case 'leave':
        currentData = leaveTakenData;
        handleChange = handleLeaveTakenDataChange;
        break;
      case 'allowances':
        currentData = allowancesData;
        handleChange = handleAllowancesDataChange;
        break;
      case 'recurring':
        currentData = recurringDeductData;
        handleChange = handleRecurringDeductDataChange;
        break;
      case 'sundry':
        currentData = sundryDeductData;
        handleChange = handleSundryDeductDataChange;
        break;
      case 'overheads':
        currentData = overheadsData;
        handleChange = handleOverheadsDataChange;
        break;
    }

    return (
      <div className="space-y-6">
        {/* Data Entry Form */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
              <input
                type="date"
                value={currentData?.date || ''}
                onChange={(e) => handleChange('date', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
                <button className="ml-2 text-blue-500 hover:text-blue-700">
                  <Icon name="info" className="w-4 h-4 inline" />
                </button>
              </label>
              <select
                value={currentData?.type || ''}
                onChange={(e) => handleChange('type', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select type...</option>
                {activeTab === 'work' && (
                  <>
                    <option value="regular">Regular Hours</option>
                    <option value="overtime">Overtime 1.5x</option>
                    <option value="sunday">Sunday 2x</option>
                  </>
                )}
                {activeTab === 'leave' && (
                  <>
                    <option value="annual">Annual Leave</option>
                    <option value="sick">Sick Leave</option>
                    <option value="personal">Personal Leave</option>
                  </>
                )}
                {activeTab === 'allowances' && (
                  <>
                    <option value="transport">Transport Allowance</option>
                    <option value="meal">Meal Allowance</option>
                    <option value="housing">Housing Allowance</option>
                  </>
                )}
                {activeTab === 'recurring' && (
                  <>
                    <option value="tax">Tax Deduction</option>
                    <option value="nasfund">NASFUND</option>
                    <option value="loan">Loan Repayment</option>
                  </>
                )}
                {activeTab === 'sundry' && (
                  <>
                    <option value="advance">Salary Advance</option>
                    <option value="fine">Fine/Penalty</option>
                    <option value="other">Other Deduction</option>
                  </>
                )}
                {activeTab === 'overheads' && (
                  <>
                    <option value="insurance">Insurance</option>
                    <option value="training">Training Cost</option>
                    <option value="equipment">Equipment</option>
                  </>
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Units</label>
              <input
                type="number"
                step="0.0001"
                value={currentData?.units || '0.0000'}
                onChange={(e) => handleChange('units', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rate</label>
              <input
                type="number"
                step="0.000001"
                value={currentData?.rate || '0.000000'}
                onChange={(e) => handleChange('rate', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Value</label>
              <input
                type="text"
                value={currentData?.value || '0.00'}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-right bg-gray-50"
              />
            </div>
          </div>
        </div>
        {/* Payslip Note */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Payslip note</label>
          <textarea
            value={payslipNote}
            onChange={(e) => setPayslipNote(e?.target?.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Add notes for payslip..."
          />
        </div>
        {/* Summary Section */}
        {payrollSummary && (
          <div className="bg-orange-50 rounded-lg border border-orange-200 p-4">
            <p className="text-sm text-orange-800">
              {payrollSummary?.regularHours?.toFixed(1)} fortnight, Casual tax: {payrollSummary?.casualTax}, Resident+{payrollSummary?.resident}
            </p>
          </div>
        )}
        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSaveData}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Icon name="plus" className="w-4 h-4" />
            {saving ? 'Saving...' : 'Add Entry'}
          </button>
          <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
            <Icon name="minus" className="w-4 h-4" />
          </button>
          <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
            <Icon name="printer" className="w-4 h-4" />
          </button>
          <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
            <Icon name="edit" className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading payroll system...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      {/* Top Bar */}
      <div className="bg-blue-600 text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Icon name="building" className="w-5 h-5" />
            <span className="font-semibold">AG INVESTMENT LIMITED</span>
          </div>
          <div className="flex items-center gap-6 ml-8">
            <button className="flex items-center gap-2 hover:bg-blue-700 px-3 py-1 rounded">
              <Icon name="home" className="w-5 h-5" />
              <span>Dashboard</span>
            </button>
            <button className="flex items-center gap-2 hover:bg-blue-700 px-3 py-1 rounded">
              <Icon name="users" className="w-5 h-5" />
              <span>Staff</span>
            </button>
            <button className="flex items-center gap-2 bg-blue-700 px-3 py-1 rounded">
              <Icon name="file-text" className="w-5 h-5" />
              <span>Payrun</span>
            </button>
            <button className="flex items-center gap-2 hover:bg-blue-700 px-3 py-1 rounded">
              <Icon name="file" className="w-5 h-5" />
              <span>Reports</span>
            </button>
            <button className="flex items-center gap-2 hover:bg-blue-700 px-3 py-1 rounded">
              <Icon name="printer" className="w-5 h-5" />
              <span>Print</span>
            </button>
            <button className="flex items-center gap-2 hover:bg-blue-700 px-3 py-1 rounded">
              <Icon name="folder" className="w-5 h-5" />
              <span>Files</span>
            </button>
            <button className="flex items-center gap-2 hover:bg-blue-700 px-3 py-1 rounded">
              <Icon name="settings" className="w-5 h-5" />
              <span>Settings</span>
            </button>
            <button className="flex items-center gap-2 hover:bg-blue-700 px-3 py-1 rounded">
              <Icon name="calendar" className="w-5 h-5" />
              <span>Pay dates</span>
            </button>
            <button className="flex items-center gap-2 hover:bg-blue-700 px-3 py-1 rounded">
              <Icon name="code" className="w-5 h-5" />
              <span>Codes</span>
            </button>
            <button className="flex items-center gap-2 hover:bg-blue-700 px-3 py-1 rounded">
              <Icon name="piggy-bank" className="w-5 h-5" />
              <span>Savings</span>
            </button>
            <button className="flex items-center gap-2 hover:bg-blue-700 px-3 py-1 rounded">
              <Icon name="book" className="w-5 h-5" />
              <span>Resources</span>
            </button>
            <button className="flex items-center gap-2 hover:bg-blue-700 px-3 py-1 rounded">
              <Icon name="wrench" className="w-5 h-5" />
              <span>Tools</span>
            </button>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <Icon name="smile" className="w-5 h-5 text-blue-600" />
            </div>
            <span>Mary Dorothy Ilau ▼</span>
          </div>
          <button className="hover:bg-blue-700 p-2 rounded">
            <Icon name="help-circle" className="w-5 h-5" />
          </button>
        </div>
      </div>
      {/* Pay Period Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-2">
        <p className="text-sm text-gray-700">{payPeriod}</p>
      </div>
      {/* Main Content */}
      <div className="flex h-[calc(100vh-180px)]">
        {/* Employee List Sidebar */}
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
          {/* Search */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Icon name="search" className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Employee List */}
          <div className="flex-1 overflow-y-auto">
            {filteredEmployees?.map((employee) => (
              <button
                key={employee?.id}
                onClick={() => handleEmployeeSelect(employee)}
                className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                  selectedEmployee?.id === employee?.id
                    ? 'bg-blue-50 border-l-4 border-l-blue-600' : employee?.employeeNumber?.startsWith('test')
                    ? 'bg-yellow-50' :''
                }`}
              >
                <div className="font-medium text-sm text-gray-900">
                  {employee?.lastName?.toUpperCase()}, {employee?.firstName}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {employee?.employeeNumber}
                </div>
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="p-3 border-t border-gray-200 flex items-center gap-2">
            <button className="flex-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-2">
              <Icon name="plus" className="w-4 h-4" />
            </button>
            <button className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center justify-center gap-2">
              <Icon name="minus" className="w-4 h-4" />
            </button>
            <button className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center justify-center gap-2">
              <Icon name="printer" className="w-4 h-4" />
            </button>
            <button className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
              <Icon name="more-horizontal" className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          {selectedEmployee ? (
            <div className="p-6">
              {/* Employee Header */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                <h2 className="text-xl font-bold text-blue-600 mb-2">
                  {selectedEmployee?.lastName?.toUpperCase()}, {selectedEmployee?.firstName}
                </h2>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Employee Number:</span>
                    <span className="ml-2 font-medium">{selectedEmployee?.employeeNumber}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Department:</span>
                    <span className="ml-2 font-medium">{selectedEmployee?.department || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Position:</span>
                    <span className="ml-2 font-medium">{selectedEmployee?.position || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Messages */}
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Icon name="alert-circle" className="w-5 h-5 text-red-600" />
                    <p className="text-red-800">{error}</p>
                  </div>
                </div>
              )}

              {successMessage && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Icon name="check-circle" className="w-5 h-5 text-green-600" />
                    <p className="text-green-800">{successMessage}</p>
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="bg-white rounded-lg border border-gray-200 mb-6">
                <div className="border-b border-gray-200">
                  <nav className="flex" aria-label="Tabs">
                    <button
                      onClick={() => setActiveTab('work')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        activeTab === 'work' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Work
                    </button>
                    <button
                      onClick={() => setActiveTab('leave')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        activeTab === 'leave' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Leave taken
                    </button>
                    <button
                      onClick={() => setActiveTab('allowances')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        activeTab === 'allowances' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Allowances
                    </button>
                    <button
                      onClick={() => setActiveTab('recurring')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        activeTab === 'recurring' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Recurring deduct
                    </button>
                    <button
                      onClick={() => setActiveTab('sundry')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        activeTab === 'sundry' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Sundry deduct
                    </button>
                    <button
                      onClick={() => setActiveTab('overheads')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        activeTab === 'overheads' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Overheads
                    </button>
                  </nav>
                </div>
              </div>

              {/* Tab Content */}
              {renderTabContent()}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <Icon name="users" className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg">Select an employee to view payroll details</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Bottom Status Bar */}
      <div className="bg-white border-t border-gray-200 px-6 py-2 flex items-center justify-end">
        <button className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2">
          <span>Activate Windows</span>
          <span className="text-xs text-gray-400">Go to Settings to activate Windows</span>
        </button>
        <button className="ml-4 px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 flex items-center gap-2">
          <Icon name="help-circle" className="w-4 h-4" />
          <span className="text-sm font-medium">Help</span>
        </button>
      </div>
    </div>
  );
};

export default TimesheetManagementPortal;